/**
 * MurmurHash3 x64_128 — isomorphic implementation (no Node.js or browser-only APIs).
 *
 * MurmurHash3 is a non-cryptographic hash function optimized for speed and
 * uniform distribution. It is suitable for hash tables, checksums, and
 * deterministic identifier generation (e.g., Datadog's resource_hash).
 *
 * NOT suitable for:
 * - Cryptographic purposes (signatures, MACs, password hashing)
 * - Hashing sensitive data, PII, or secrets — use a cryptographic hash (SHA-256)
 *   or an HMAC for those cases
 *
 * MurmurHash3 is trivially reversible for short inputs and has no resistance
 * to collision attacks. Use it only for non-security-sensitive identifiers.
 *
 * Our use case: computing Datadog APM resource_hash values to build resource
 * page URLs (see compute_sql_signature in DataDog/integrations-core).
 */

const MASK = 0xffffffffffffffffn;
const C1 = 0x87c37b91114253d5n;
const C2 = 0x4cf5ad432745937fn;

function rotl64(x: bigint, r: bigint): bigint {
  return ((x << r) | (x >> (64n - r))) & MASK;
}

function fmix64(k: bigint): bigint {
  k = (k ^ (k >> 33n)) & MASK;
  k = (k * 0xff51afd7ed558ccdn) & MASK;
  k = (k ^ (k >> 33n)) & MASK;
  k = (k * 0xc4ceb9fe1a85ec53n) & MASK;
  k = (k ^ (k >> 33n)) & MASK;
  return k;
}

/** Read a little-endian uint64 from a byte array at the given offset. */
function readU64LE(bytes: Uint8Array, offset: number): bigint {
  let val = 0n;
  for (let i = 7; i >= 0; i--) {
    val = (val << 8n) | BigInt(bytes[offset + i]);
  }
  return val;
}

/**
 * Compute the first 64 bits of MurmurHash3_x64_128 with seed 0.
 * Returns an unsigned hex string (no leading zeros padding).
 */
export function murmurhash3_resource(input: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(input);
  const len = bytes.length;
  const nblocks = Math.floor(len / 16);

  let h1 = 0n;
  let h2 = 0n;

  // Body — process 16-byte blocks
  for (let i = 0; i < nblocks; i++) {
    let k1 = readU64LE(bytes, i * 16);
    let k2 = readU64LE(bytes, i * 16 + 8);

    k1 = (k1 * C1) & MASK;
    k1 = rotl64(k1, 31n);
    k1 = (k1 * C2) & MASK;
    h1 = (h1 ^ k1) & MASK;
    h1 = rotl64(h1, 27n);
    h1 = (h1 + h2) & MASK;
    h1 = (h1 * 5n + 0x52dce729n) & MASK;

    k2 = (k2 * C2) & MASK;
    k2 = rotl64(k2, 33n);
    k2 = (k2 * C1) & MASK;
    h2 = (h2 ^ k2) & MASK;
    h2 = rotl64(h2, 31n);
    h2 = (h2 + h1) & MASK;
    h2 = (h2 * 5n + 0x38495ab5n) & MASK;
  }

  // Tail — read remaining bytes into k1 (bytes 0–7) and k2 (bytes 8–14)
  const tailOffset = nblocks * 16;
  const tailLen = len - tailOffset;
  let k1 = 0n;
  let k2 = 0n;

  if (tailLen >= 15) k2 ^= BigInt(bytes[tailOffset + 14]) << 48n;
  if (tailLen >= 14) k2 ^= BigInt(bytes[tailOffset + 13]) << 40n;
  if (tailLen >= 13) k2 ^= BigInt(bytes[tailOffset + 12]) << 32n;
  if (tailLen >= 12) k2 ^= BigInt(bytes[tailOffset + 11]) << 24n;
  if (tailLen >= 11) k2 ^= BigInt(bytes[tailOffset + 10]) << 16n;
  if (tailLen >= 10) k2 ^= BigInt(bytes[tailOffset + 9]) << 8n;
  if (tailLen >= 9) {
    k2 ^= BigInt(bytes[tailOffset + 8]);
    k2 = (k2 * C2) & MASK;
    k2 = rotl64(k2, 33n);
    k2 = (k2 * C1) & MASK;
    h2 = (h2 ^ k2) & MASK;
  }

  if (tailLen >= 8) k1 ^= BigInt(bytes[tailOffset + 7]) << 56n;
  if (tailLen >= 7) k1 ^= BigInt(bytes[tailOffset + 6]) << 48n;
  if (tailLen >= 6) k1 ^= BigInt(bytes[tailOffset + 5]) << 40n;
  if (tailLen >= 5) k1 ^= BigInt(bytes[tailOffset + 4]) << 32n;
  if (tailLen >= 4) k1 ^= BigInt(bytes[tailOffset + 3]) << 24n;
  if (tailLen >= 3) k1 ^= BigInt(bytes[tailOffset + 2]) << 16n;
  if (tailLen >= 2) k1 ^= BigInt(bytes[tailOffset + 1]) << 8n;
  if (tailLen >= 1) {
    k1 ^= BigInt(bytes[tailOffset]);
    k1 = (k1 * C1) & MASK;
    k1 = rotl64(k1, 31n);
    k1 = (k1 * C2) & MASK;
    h1 = (h1 ^ k1) & MASK;
  }

  // Finalization
  const blen = BigInt(len);
  h1 = (h1 ^ blen) & MASK;
  h2 = (h2 ^ blen) & MASK;
  h1 = (h1 + h2) & MASK;
  h2 = (h2 + h1) & MASK;
  h1 = fmix64(h1);
  h2 = fmix64(h2);
  h1 = (h1 + h2) & MASK;

  return h1.toString(16);
}
